import { FieldLayout, fieldClassName } from "./input-field";
import { useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@verse_core/shared_components/ClassName";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import useClickOutside from "@verse_core/shared_components/useClickOutside";

type RadioGridSelectProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  // Called when the panel closes, so the field counts as visited.
  onBlur?: () => void;
  error?: string | null;
  inline?: boolean;
  placeholder?: string;
  // Extra classes for the closed box, e.g. a narrower width.
  inputClassName?: string;
};

/** Rebrand dropdown that opens a panel of radio buttons in a grid, for a long
 * list of short choices, e.g. the sign-up facility type. Clicking a choice,
 * Enter, Escape, or leaving the panel closes it. */
export function RadioGridSelect({
  id,
  label,
  value,
  onChange,
  options,
  onBlur,
  error,
  inline,
  placeholder = "(select)",
  inputClassName,
}: RadioGridSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = () => {
    if (!open) return;
    setOpen(false);
    onBlur?.();
  };
  const closeAndFocus = () => {
    close();
    buttonRef.current?.focus();
  };
  useClickOutside(containerRef, close);

  const selected = options.find((option) => option.value === value);

  return (
    <FieldLayout id={id} label={label} error={error} inline={inline}>
      <div
        ref={containerRef}
        className="tw-relative"
        onKeyDown={(event) => {
          // Enter would otherwise submit the form.
          if ((event.key === "Escape" || event.key === "Enter") && open) {
            event.preventDefault();
            closeAndFocus();
          }
        }}
        onBlur={(event) => {
          if (!containerRef.current?.contains(event.relatedTarget)) close();
        }}
      >
        <button
          ref={buttonRef}
          id={id}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onClick={() => (open ? close() : setOpen(true))}
          className={cn(
            fieldClassName(error),
            "tw-flex tw-items-center tw-justify-between tw-text-left",
            !selected && "tw-text-verse-text-disabled",
            inputClassName,
          )}
        >
          <span className="tw-truncate">{selected?.label ?? placeholder}</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            aria-hidden="true"
            className="tw-h-[16px] tw-w-[16px] tw-shrink-0 tw-text-verse-icon-primary"
          />
        </button>
        {open && (
          <div
            role="radiogroup"
            aria-label={label}
            // Keep focus on the button, so pressing an option doesn't blur
            // and close the panel before the click lands.
            onMouseDown={(event) => event.preventDefault()}
            className="tw-absolute tw-left-0 tw-top-full tw-z-10 tw-mt-[4px] tw-grid tw-w-[560px] tw-max-w-[calc(100vw-32px)] tw-grid-cols-3 tw-gap-[12px] tw-rounded-[8px] tw-bg-white tw-p-[16px] tw-shadow-[0_1px_4px_rgba(0,0,0,0.12)] 720:tw-grid-cols-2"
          >
            {options.map((option) => (
              <label
                key={option.value}
                // A mouse click (detail > 0) picks and closes; arrow keys also
                // click radios, and should just move the selection. Pick here
                // rather than through the radio, which closing removes before
                // a click on the name reaches it.
                onClick={(event) => {
                  if (event.detail === 0) return;
                  event.preventDefault();
                  onChange(option.value);
                  closeAndFocus();
                }}
                // font-normal: the provider app bolds every label.
                className="tw-m-0 tw-flex tw-cursor-pointer tw-items-start tw-gap-[8px] tw-font-sohne tw-text-[14px] tw-font-normal tw-leading-[1.4] tw-text-verse-text-primary"
              >
                <input
                  type="radio"
                  name={id}
                  value={option.value}
                  checked={option.value === value}
                  onChange={() => onChange(option.value)}
                  className="tw-m-0 tw-mt-[2px] tw-h-[16px] tw-w-[16px] tw-shrink-0 tw-cursor-pointer tw-accent-verse-tide"
                />
                {option.label}
              </label>
            ))}
          </div>
        )}
      </div>
    </FieldLayout>
  );
}
